import { useState, useMemo, useCallback } from 'react';
import { TenantUser } from '../../types';
import { SecuritySnapshot, getAvailableLicenses, AvailableLicense, bulkAssignLicense, bulkResetPasswords, bulkToggleUsers, BulkOperationResponse } from '../../services/api';
import { Search, User, Mail, ChevronRight, UserPlus, ShieldCheck, ShieldAlert, Key, Shield, UserCheck, UserX, X, CheckSquare, Square, Loader2 } from 'lucide-react';
import UserDetail from './UserDetail';
import CreateUserModal from './CreateUserModal';

// Shorten license names for display
function shortenLicenseName(name: string): string {
  return name
    .replace(/^Microsoft 365 /i, 'M365 ')
    .replace(/^Office 365 /i, 'O365 ')
    .replace(/^Enterprise Mobility \+ Security /i, 'EMS ')
    .replace(/^Azure Active Directory /i, 'AAD ')
    .replace(/^Azure AD /i, 'AAD ')
    .replace(/^Microsoft Defender for /i, 'Defender ')
    .replace(/^Microsoft /i, '')
    .replace(/^Dynamics 365 /i, 'D365 ');
}

interface UserListProps {
  users: TenantUser[];
  loading: boolean;
  error: string | null;
  clientId: string;
  clientName: string;
  tenantId?: string;
  onUserUpdated?: () => void;
  securitySnapshot?: SecuritySnapshot | null;
}

// ---- Bulk License Assignment Modal ----
function BulkLicenseModal({
  isOpen,
  onClose,
  clientId,
  selectedCount,
  selectedUserIds,
  onComplete,
}: {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  selectedCount: number;
  selectedUserIds: string[];
  onComplete: (result: BulkOperationResponse) => void;
}) {
  const [skus, setSkus] = useState<AvailableLicense[]>([]);
  const [selectedSkuId, setSelectedSkuId] = useState('');
  const [loadingSkus, setLoadingSkus] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  // Load SKUs when modal opens
  useState(() => {
    if (isOpen) {
      setLoadingSkus(true);
      getAvailableLicenses(clientId)
        .then(data => {
          setSkus(data.items || []);
          setLoadingSkus(false);
        })
        .catch(() => setLoadingSkus(false));
    }
  });

  const handleAssign = async () => {
    if (!selectedSkuId) return;
    setProcessing(true);
    setProgress({ done: 0, total: selectedUserIds.length });

    try {
      const result = await bulkAssignLicense(clientId, selectedUserIds, selectedSkuId);
      setProgress({ done: result.summary.total, total: result.summary.total });
      onComplete(result);
    } catch (err) {
      onComplete({
        success: false,
        results: [],
        summary: { total: selectedUserIds.length, succeeded: 0, failed: selectedUserIds.length },
        error: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setProcessing(false);
      setProgress(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Assign License</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-muted-foreground">
            Select a license to assign to <strong>{selectedCount}</strong> selected user{selectedCount > 1 ? 's' : ''}.
          </p>
          {loadingSkus ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading available licenses...
            </div>
          ) : (
            <select
              value={selectedSkuId}
              onChange={e => setSelectedSkuId(e.target.value)}
              className="w-full px-4 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select a license...</option>
              {skus.map(sku => (
                <option key={sku.msSkuId} value={sku.msSkuId}>
                  {sku.productName} ({sku.availableLicenses} available of {sku.totalLicenses})
                </option>
              ))}
            </select>
          )}
          {progress && (
            <div className="space-y-2">
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(progress.done / progress.total) * 100}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground text-center">{progress.done} of {progress.total} completed</p>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border">
          <button onClick={onClose} disabled={processing} className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={!selectedSkuId || processing}
            className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {processing && <Loader2 className="h-4 w-4 animate-spin" />}
            Assign to {selectedCount} user{selectedCount > 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Bulk Password Reset Modal ----
function BulkPasswordResetModal({
  isOpen,
  onClose,
  clientId,
  selectedCount,
  selectedUserIds,
  onComplete,
}: {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  selectedCount: number;
  selectedUserIds: string[];
  onComplete: (result: BulkOperationResponse) => void;
}) {
  const [sendEmail, setSendEmail] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const handleReset = async () => {
    setProcessing(true);
    setProgress({ done: 0, total: selectedUserIds.length });

    try {
      const result = await bulkResetPasswords(clientId, selectedUserIds, sendEmail);
      setProgress({ done: result.summary.total, total: result.summary.total });
      onComplete(result);
    } catch (err) {
      onComplete({
        success: false,
        results: [],
        summary: { total: selectedUserIds.length, succeeded: 0, failed: selectedUserIds.length },
        error: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setProcessing(false);
      setProgress(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Reset Passwords</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Reset passwords for <strong>{selectedCount}</strong> user{selectedCount > 1 ? 's' : ''}? Each user will receive a randomly generated password and be forced to change it on next sign-in.
            </p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={e => setSendEmail(e.target.checked)}
              className="rounded border-border text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-foreground">Send passwords via email</span>
          </label>
          {progress && (
            <div className="space-y-2">
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(progress.done / progress.total) * 100}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground text-center">{progress.done} of {progress.total} completed</p>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border">
          <button onClick={onClose} disabled={processing} className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
            Cancel
          </button>
          <button
            onClick={handleReset}
            disabled={processing}
            className="px-4 py-2 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {processing && <Loader2 className="h-4 w-4 animate-spin" />}
            Reset {selectedCount} Password{selectedCount > 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Bulk Toggle (Enable/Disable) Modal ----
function BulkToggleModal({
  isOpen,
  onClose,
  clientId,
  selectedCount,
  selectedUserIds,
  enabled,
  onComplete,
}: {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  selectedCount: number;
  selectedUserIds: string[];
  enabled: boolean;
  onComplete: (result: BulkOperationResponse) => void;
}) {
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const handleToggle = async () => {
    setProcessing(true);
    setProgress({ done: 0, total: selectedUserIds.length });

    try {
      const result = await bulkToggleUsers(clientId, selectedUserIds, enabled);
      setProgress({ done: result.summary.total, total: result.summary.total });
      onComplete(result);
    } catch (err) {
      onComplete({
        success: false,
        results: [],
        summary: { total: selectedUserIds.length, succeeded: 0, failed: selectedUserIds.length },
        error: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setProcessing(false);
      setProgress(null);
    }
  };

  if (!isOpen) return null;

  const action = enabled ? 'Enable' : 'Disable';
  const colorClass = enabled ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">{action} Users</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div className={`${enabled ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'} border rounded-lg p-3`}>
            <p className={`text-sm ${enabled ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
              {enabled
                ? <>Are you sure you want to <strong>enable</strong> {selectedCount} user account{selectedCount > 1 ? 's' : ''}? They will be able to sign in again.</>
                : <>Are you sure you want to <strong>disable</strong> {selectedCount} user account{selectedCount > 1 ? 's' : ''}? They will be blocked from signing in.</>
              }
            </p>
          </div>
          {progress && (
            <div className="space-y-2">
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(progress.done / progress.total) * 100}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground text-center">{progress.done} of {progress.total} completed</p>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border">
          <button onClick={onClose} disabled={processing} className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
            Cancel
          </button>
          <button
            onClick={handleToggle}
            disabled={processing}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${colorClass}`}
          >
            {processing && <Loader2 className="h-4 w-4 animate-spin" />}
            {action} {selectedCount} User{selectedCount > 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Results Toast ----
function BulkResultsToast({
  result,
  operationName,
  onClose,
}: {
  result: BulkOperationResponse;
  operationName: string;
  onClose: () => void;
}) {
  const isAllSuccess = result.summary.failed === 0;
  const isAllFailed = result.summary.succeeded === 0;

  return (
    <div className={`fixed bottom-20 left-1/2 -translate-x-1/2 z-50 px-6 py-4 rounded-xl shadow-xl border max-w-md w-full mx-4 ${
      isAllSuccess
        ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800'
        : isAllFailed
        ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800'
        : 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800'
    }`}>
      <div className="flex items-start justify-between">
        <div>
          <p className={`font-medium text-sm ${
            isAllSuccess ? 'text-green-800 dark:text-green-200'
              : isAllFailed ? 'text-red-800 dark:text-red-200'
              : 'text-amber-800 dark:text-amber-200'
          }`}>
            {operationName} Complete
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {result.summary.succeeded} succeeded, {result.summary.failed} failed of {result.summary.total} total
          </p>
          {result.error && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">{result.error}</p>
          )}
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground ml-4">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

type SortColumn = 'displayName' | 'jobTitle' | 'licenses' | 'roles' | 'mfa' | null;
type SortDirection = 'asc' | 'desc' | null;

export default function UserList({ users, loading, error, clientId, clientName, tenantId, onUserUpdated, securitySnapshot }: UserListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLicenseFilter, setSelectedLicenseFilter] = useState<string>('all');
  const [selectedMfaFilter, setSelectedMfaFilter] = useState<string>('all');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  // Bulk selection state
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showToggleModal, setShowToggleModal] = useState<'enable' | 'disable' | null>(null);
  const [bulkResult, setBulkResult] = useState<{ result: BulkOperationResponse; name: string } | null>(null);

  // Create a map of admin UPNs to their MFA status
  const adminMfaMap = useMemo(() => {
    const map = new Map<string, boolean>();
    if (securitySnapshot?.admins?.list) {
      securitySnapshot.admins.list.forEach(admin => {
        if (admin.userPrincipalName) {
          map.set(admin.userPrincipalName.toLowerCase(), admin.mfaRegistered);
        }
      });
    }
    return map;
  }, [securitySnapshot]);

  // Determine if we have any MFA data to show (from security snapshot or user objects)
  const hasMfaData = useMemo(() => {
    if (securitySnapshot) return true;
    return users.some(u => u.mfaRegistered !== undefined);
  }, [securitySnapshot, users]);

  // Get unique license names for filter dropdown
  const uniqueLicenses = useMemo(() => {
    const licenseSet = new Set<string>();
    users.forEach(user => {
      user.assignedLicenses.forEach(lic => {
        licenseSet.add(lic.skuName);
      });
    });
    return Array.from(licenseSet).sort();
  }, [users]);

  // Helper to get MFA status for a user
  const getUserMfaStatus = (user: TenantUser): 'yes' | 'no' | 'unknown' => {
    // First check admin map (from security snapshot)
    const upn = user.userPrincipalName.toLowerCase();
    if (adminMfaMap.has(upn)) {
      return adminMfaMap.get(upn) ? 'yes' : 'no';
    }
    // Fall back to per-user mfaRegistered field from Graph API
    if (user.mfaRegistered !== undefined) {
      return user.mfaRegistered ? 'yes' : 'no';
    }
    return 'unknown';
  };

  // Toggle sort: asc -> desc -> clear
  const handleSort = useCallback((column: SortColumn) => {
    if (sortColumn !== column) {
      setSortColumn(column);
      setSortDirection('asc');
    } else if (sortDirection === 'asc') {
      setSortDirection('desc');
    } else {
      setSortColumn(null);
      setSortDirection(null);
    }
  }, [sortColumn, sortDirection]);

  // Filter users based on search, license, and MFA filter, then sort
  const filteredUsers = useMemo(() => {
    const filtered = users.filter(user => {
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        !searchTerm ||
        user.displayName.toLowerCase().includes(searchLower) ||
        user.userPrincipalName.toLowerCase().includes(searchLower) ||
        (user.mail && user.mail.toLowerCase().includes(searchLower)) ||
        (user.jobTitle && user.jobTitle.toLowerCase().includes(searchLower)) ||
        (user.department && user.department.toLowerCase().includes(searchLower));

      // License filter
      const matchesLicense =
        selectedLicenseFilter === 'all' ||
        (selectedLicenseFilter === 'none' && user.assignedLicenses.length === 0) ||
        user.assignedLicenses.some(lic => lic.skuName === selectedLicenseFilter);

      // MFA filter (only applies to users we have MFA data for)
      const userMfaStatus = getUserMfaStatus(user);
      const matchesMfa =
        selectedMfaFilter === 'all' ||
        (selectedMfaFilter === 'mfa-yes' && userMfaStatus === 'yes') ||
        (selectedMfaFilter === 'mfa-no' && userMfaStatus === 'no') ||
        (selectedMfaFilter === 'admin' && adminMfaMap.has(user.userPrincipalName.toLowerCase()));

      return matchesSearch && matchesLicense && matchesMfa;
    });

    // Sort the filtered results
    const sorted = [...filtered];
    if (sortColumn && sortDirection) {
      const dir = sortDirection === 'asc' ? 1 : -1;
      sorted.sort((a, b) => {
        let cmp = 0;
        switch (sortColumn) {
          case 'displayName':
            cmp = a.displayName.localeCompare(b.displayName);
            break;
          case 'jobTitle':
            cmp = (a.jobTitle || '').localeCompare(b.jobTitle || '');
            break;
          case 'licenses':
            cmp = a.assignedLicenses.length - b.assignedLicenses.length;
            break;
          case 'roles':
            cmp = (a.directoryRoles?.length || 0) - (b.directoryRoles?.length || 0);
            break;
          case 'mfa': {
            const mfaOrder = { yes: 2, no: 1, unknown: 0 };
            cmp = mfaOrder[getUserMfaStatus(a)] - mfaOrder[getUserMfaStatus(b)];
            break;
          }
        }
        return cmp * dir;
      });
    } else {
      // Default sort: users with roles first, then by displayName
      sorted.sort((a, b) => {
        const aRoles = a.directoryRoles?.length || 0;
        const bRoles = b.directoryRoles?.length || 0;
        if (aRoles > 0 && bRoles === 0) return -1;
        if (aRoles === 0 && bRoles > 0) return 1;
        return a.displayName.localeCompare(b.displayName);
      });
    }
    return sorted;
  }, [users, searchTerm, selectedLicenseFilter, selectedMfaFilter, adminMfaMap, sortColumn, sortDirection]);

  // Bulk selection helpers
  const filteredUserIds = useMemo(() => new Set(filteredUsers.map(u => u.id)), [filteredUsers]);

  const isAllSelected = filteredUsers.length > 0 && filteredUsers.every(u => selectedUserIds.has(u.id));
  const isSomeSelected = selectedUserIds.size > 0;

  const toggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(filteredUsers.map(u => u.id)));
    }
  }, [isAllSelected, filteredUsers]);

  const toggleSelectUser = useCallback((userId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }, []);

  // Only count selected users that are currently visible (filtered)
  const visibleSelectedIds = useMemo(() => {
    return Array.from(selectedUserIds).filter(id => filteredUserIds.has(id));
  }, [selectedUserIds, filteredUserIds]);

  const handleBulkComplete = (result: BulkOperationResponse, operationName: string) => {
    setBulkResult({ result, name: operationName });
    setShowLicenseModal(false);
    setShowPasswordModal(false);
    setShowToggleModal(null);
    setSelectedUserIds(new Set());
    onUserUpdated?.();
    // Auto-dismiss toast after 8 seconds
    setTimeout(() => setBulkResult(null), 8000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-3 text-muted-foreground">Loading users...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-700 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-12">
        <User className="h-12 w-12 mx-auto text-muted-foreground" />
        <p className="mt-2 text-muted-foreground">No users found</p>
        <p className="text-sm text-muted-foreground">
          This client may not have a Microsoft Tenant ID configured
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* License Filter */}
        <select
          value={selectedLicenseFilter}
          onChange={(e) => setSelectedLicenseFilter(e.target.value)}
          className="px-4 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="all">All Licenses</option>
          <option value="none">No Licenses</option>
          {uniqueLicenses.map(lic => (
            <option key={lic} value={lic}>{lic}</option>
          ))}
        </select>

        {/* MFA Filter - show if we have any MFA data */}
        {hasMfaData && (
          <select
            value={selectedMfaFilter}
            onChange={(e) => setSelectedMfaFilter(e.target.value)}
            className="px-4 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Users</option>
            <option value="admin">Admins Only</option>
            <option value="mfa-yes">MFA Enabled</option>
            <option value="mfa-no">MFA Disabled</option>
          </select>
        )}

        {/* Create User Button */}
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2 font-medium"
        >
          <UserPlus className="h-5 w-5" />
          <span className="hidden sm:inline">Create User</span>
        </button>
      </div>

      {/* Results count */}
      <p className="text-sm text-muted-foreground">
        Showing {filteredUsers.length} of {users.length} users
        {isSomeSelected && <span className="ml-2 text-primary-600 font-medium">({visibleSelectedIds.length} selected)</span>}
      </p>

      {/* User List */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted">
            <tr>
              <th className="px-3 py-3 w-10">
                <button
                  onClick={toggleSelectAll}
                  className="text-muted-foreground hover:text-foreground"
                  title={isAllSelected ? 'Deselect all' : 'Select all'}
                >
                  {isAllSelected ? (
                    <CheckSquare className="h-5 w-5 text-primary-600" />
                  ) : (
                    <Square className="h-5 w-5" />
                  )}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer select-none hover:text-foreground" onClick={() => handleSort('displayName')}>
                User {sortColumn === 'displayName' ? (sortDirection === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden sm:table-cell cursor-pointer select-none hover:text-foreground" onClick={() => handleSort('jobTitle')}>
                Job Title {sortColumn === 'jobTitle' ? (sortDirection === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer select-none hover:text-foreground" onClick={() => handleSort('licenses')}>
                Licenses {sortColumn === 'licenses' ? (sortDirection === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden lg:table-cell cursor-pointer select-none hover:text-foreground" onClick={() => handleSort('roles')}>
                Roles {sortColumn === 'roles' ? (sortDirection === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
              </th>
              {hasMfaData && (
                <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider hidden md:table-cell cursor-pointer select-none hover:text-foreground" onClick={() => handleSort('mfa')}>
                  MFA {sortColumn === 'mfa' ? (sortDirection === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                </th>
              )}
              <th className="px-4 py-3 w-10">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredUsers.map(user => (
              <tr
                key={user.id}
                onClick={() => setSelectedUserId(user.id)}
                className={`hover:bg-accent cursor-pointer ${selectedUserIds.has(user.id) ? 'bg-primary-50 dark:bg-primary-900/20' : ''}`}
              >
                <td className="px-3 py-3">
                  <button
                    onClick={(e) => toggleSelectUser(user.id, e)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {selectedUserIds.has(user.id) ? (
                      <CheckSquare className="h-5 w-5 text-primary-600" />
                    ) : (
                      <Square className="h-5 w-5" />
                    )}
                  </button>
                </td>
                <td className="px-4 py-3" style={{ maxWidth: '200px' }}>
                  <div className="flex items-center min-w-0">
                    <div className="flex-shrink-0 h-10 w-10 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                      <span className="text-primary-700 dark:text-primary-300 font-medium text-sm">
                        {user.displayName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <div className="ml-3 min-w-0">
                      <p className="text-sm font-medium text-foreground overflow-hidden text-ellipsis whitespace-nowrap" title={user.displayName}>
                        {user.displayName}
                      </p>
                      <div className="flex items-center text-sm text-muted-foreground min-w-0" title={user.mail || user.userPrincipalName}>
                        <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap">{user.mail || user.userPrincipalName}</span>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 hidden sm:table-cell" style={{ maxWidth: '120px' }}>
                  <p className="text-sm text-foreground overflow-hidden text-ellipsis whitespace-nowrap" title={user.jobTitle || '-'}>
                    {user.jobTitle || '-'}
                  </p>
                  {user.department && (
                    <p className="text-xs text-muted-foreground overflow-hidden text-ellipsis whitespace-nowrap" title={user.department}>
                      {user.department}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {user.assignedLicenses.length === 0 ? (
                      <span className="text-sm text-muted-foreground italic">
                        No licenses
                      </span>
                    ) : (
                      <>
                        {user.assignedLicenses.slice(0, 3).map((lic, idx) => {
                          const shortName = shortenLicenseName(lic.skuName);
                          return (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                              title={lic.skuName}
                            >
                              {shortName.length > 20 ? shortName.substring(0, 20) + '...' : shortName}
                            </span>
                          );
                        })}
                        {user.assignedLicenses.length > 3 && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-foreground"
                            title={user.assignedLicenses.slice(3).map(l => l.skuName).join(', ')}
                          >
                            +{user.assignedLicenses.length - 3} more
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 hidden lg:table-cell" style={{ maxWidth: '180px' }}>
                  <div className="flex flex-wrap gap-1">
                    {user.directoryRoles && user.directoryRoles.length > 0 ? (
                      <>
                        {user.directoryRoles.slice(0, 2).map((role, idx) => {
                          const isGlobalAdmin = role.displayName.toLowerCase().includes('global administrator');
                          return (
                            <span
                              key={idx}
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                isGlobalAdmin
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                  : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                              }`}
                              title={role.displayName}
                            >
                              {role.displayName.replace(/ Administrator$/, ' Admin').length > 22
                                ? role.displayName.replace(/ Administrator$/, ' Admin').substring(0, 20) + '...'
                                : role.displayName.replace(/ Administrator$/, ' Admin')}
                            </span>
                          );
                        })}
                        {user.directoryRoles.length > 2 && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-foreground"
                            title={user.directoryRoles.slice(2).map(r => r.displayName).join(', ')}
                          >
                            +{user.directoryRoles.length - 2} more
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                  </div>
                </td>
                {hasMfaData && (
                  <td className="px-4 py-3 text-center hidden md:table-cell">
                    {(() => {
                      const mfaStatus = getUserMfaStatus(user);
                      const isAdmin = adminMfaMap.has(user.userPrincipalName.toLowerCase());
                      if (mfaStatus === 'unknown') {
                        return <span className="text-muted-foreground text-sm">-</span>;
                      }
                      if (isAdmin) {
                        // Admin users get prominent shield icons
                        if (mfaStatus === 'yes') {
                          return (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" title="MFA Enabled (Admin)">
                              <ShieldCheck className="h-3.5 w-3.5" />
                              MFA
                            </span>
                          );
                        } else {
                          return (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" title="MFA Disabled (Admin)">
                              <ShieldAlert className="h-3.5 w-3.5" />
                              No MFA
                            </span>
                          );
                        }
                      }
                      // Non-admin users get simpler indicators
                      if (mfaStatus === 'yes') {
                        return (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" title="MFA Enabled">
                            MFA
                          </span>
                        );
                      } else {
                        return (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" title="MFA Not Registered">
                            No MFA
                          </span>
                        );
                      }
                    })()}
                  </td>
                )}
                <td className="px-4 py-3 text-right">
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bottom Action Bar - shown when users are selected */}
      {visibleSelectedIds.length > 0 && (
        <div className="fixed bottom-0 left-1/2 -translate-x-1/2 z-50 bg-gray-900 dark:bg-gray-950 border border-gray-700 dark:border-gray-600 rounded-t-xl shadow-2xl px-6 py-3 flex items-center gap-4">
          <span className="text-sm font-medium text-white whitespace-nowrap">
            {visibleSelectedIds.length} user{visibleSelectedIds.length > 1 ? 's' : ''} selected
          </span>
          <div className="h-6 w-px bg-gray-600" />
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowLicenseModal(true)}
              className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1.5 transition-colors"
            >
              <Shield className="h-3.5 w-3.5" />
              Assign License
            </button>
            <button
              onClick={() => setShowPasswordModal(true)}
              className="px-3 py-1.5 text-xs font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-1.5 transition-colors"
            >
              <Key className="h-3.5 w-3.5" />
              Reset Passwords
            </button>
            <button
              onClick={() => setShowToggleModal('enable')}
              className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1.5 transition-colors"
            >
              <UserCheck className="h-3.5 w-3.5" />
              Enable
            </button>
            <button
              onClick={() => setShowToggleModal('disable')}
              className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-1.5 transition-colors"
            >
              <UserX className="h-3.5 w-3.5" />
              Disable
            </button>
          </div>
          <div className="h-6 w-px bg-gray-600" />
          <button
            onClick={() => setSelectedUserIds(new Set())}
            className="text-gray-400 hover:text-white transition-colors"
            title="Clear selection"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Bulk Operation Modals */}
      <BulkLicenseModal
        isOpen={showLicenseModal}
        onClose={() => setShowLicenseModal(false)}
        clientId={clientId}
        selectedCount={visibleSelectedIds.length}
        selectedUserIds={visibleSelectedIds}
        onComplete={(result) => handleBulkComplete(result, 'License Assignment')}
      />

      <BulkPasswordResetModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        clientId={clientId}
        selectedCount={visibleSelectedIds.length}
        selectedUserIds={visibleSelectedIds}
        onComplete={(result) => handleBulkComplete(result, 'Password Reset')}
      />

      {showToggleModal && (
        <BulkToggleModal
          isOpen={true}
          onClose={() => setShowToggleModal(null)}
          clientId={clientId}
          selectedCount={visibleSelectedIds.length}
          selectedUserIds={visibleSelectedIds}
          enabled={showToggleModal === 'enable'}
          onComplete={(result) => handleBulkComplete(result, showToggleModal === 'enable' ? 'Enable Users' : 'Disable Users')}
        />
      )}

      {/* Results Toast */}
      {bulkResult && (
        <BulkResultsToast
          result={bulkResult.result}
          operationName={bulkResult.name}
          onClose={() => setBulkResult(null)}
        />
      )}

      {/* User Detail Modal */}
      {selectedUserId && (
        <UserDetail
          clientId={clientId}
          clientName={clientName}
          userId={selectedUserId}
          isOpen={!!selectedUserId}
          onClose={() => setSelectedUserId(null)}
          onUserUpdated={onUserUpdated}
        />
      )}

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        clientId={clientId}
        clientName={clientName}
        tenantId={tenantId}
        existingUsers={users}
        onUserCreated={() => {
          setShowCreateModal(false);
          onUserUpdated?.();
        }}
      />
    </div>
  );
}
